import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";

const LANGUAGES = [
  { code: "it", label: "🇮🇹 Italiano" },
  { code: "en", label: "🇬🇧 English" },
  { code: "fr", label: "🇫🇷 Français" },
  { code: "es", label: "🇪🇸 Español" },
];

/**
 * Compact dialog for changing the current user's password.
 * If `forced=true`, skips the "current password" field, shows a language selector,
 * and cannot be closed until submitted successfully.
 */
export function ChangePasswordDialog({ open, onOpenChange, forced = false, onDone }) {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [language, setLanguage] = useState("it");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && forced) setLanguage(user?.language || "it");
  }, [open, forced, user?.language]);

  const reset = () => { setCurrent(""); setNext(""); setConfirm(""); };

  const submit = async () => {
    if (!next || next.length < 6) { toast.error("Password minimo 6 caratteri"); return; }
    if (next !== confirm) { toast.error("Le due password non coincidono"); return; }
    if (!forced && !current) { toast.error("Inserisci la password attuale"); return; }
    setSaving(true);
    try {
      const payload = forced
        ? { new_password: next, language }
        : { current_password: current, new_password: next };
      await api.post("/auth/change-password", payload);
      toast.success("Password aggiornata");
      reset();
      onOpenChange(false);
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => { if (!forced) onOpenChange(v); }}
    >
      <DialogContent className="rounded-sm max-w-md" data-testid="change-password-dialog" onEscapeKeyDown={(e) => forced && e.preventDefault()} onPointerDownOutside={(e) => forced && e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-brand" /> {forced ? "Imposta la tua nuova password" : "Cambia password"}
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            {forced ? "Per motivi di sicurezza, imposta una tua password e scegli la lingua che preferisci prima di continuare." : "Cambia la password del tuo account."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {!forced && (
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Password attuale</Label>
              <Input data-testid="chpwd-current" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className="mt-1 rounded-sm" />
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Nuova password</Label>
            <Input data-testid="chpwd-new" type="password" value={next} onChange={(e) => setNext(e.target.value)} className="mt-1 rounded-sm" placeholder="Minimo 6 caratteri" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Conferma nuova password</Label>
            <Input data-testid="chpwd-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1 rounded-sm" />
          </div>
          {forced && (
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Lingua preferita</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger data-testid="chpwd-language" className="mt-1 rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGES.map((l) => (
                    <SelectItem key={l.code} value={l.code} data-testid={`chpwd-language-${l.code}`}>{l.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-[11px] text-zinc-500">Sarà usata per l&apos;interfaccia e per le risposte AI.</p>
            </div>
          )}
        </div>
        <DialogFooter>
          {!forced && <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} disabled={saving}>Annulla</Button>}
          <Button data-testid="chpwd-submit" onClick={submit} disabled={saving} className="bg-brand hover:bg-brand-dark text-white rounded-sm">
            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Salvo...</> : "Salva password"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
